import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

import type { JobTemplatesData } from '@core/job-templates-types';
import type { OfferFull } from '@core/offers-types';

import { JobTemplatesFullActions, JobTemplatesFullSelectors } from './job-templates-full.store';

@Injectable({
  providedIn: 'root',
})
export class JobTemplatesFullDataService {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  public fetchOne(id: number, langCode?: string): void {
    const payload = { id, langCode };
    this.store.dispatch(JobTemplatesFullActions.fetchOne({ payload }));
  }

  public fetchOneSuccessAction() {
    return this.actions$.pipe(ofType(JobTemplatesFullActions.fetchOneSuccess));
  }

  public getFullOneById(id: number) {
    return this.store.select(JobTemplatesFullSelectors.byIdWithReferences(id)).pipe(
      map(jobTemplate => {
        if (!jobTemplate) {
          return jobTemplate;
        }

        return {
          ...jobTemplate,
          admins: jobTemplate.admins.filter(admin => admin),
        };
      }),
    );
  }

  public getMetaOneById(id: number) {
    return this.store.select(JobTemplatesFullSelectors.meta(id));
  }

  public getIsFullOnePending(id: number) {
    return this.store.select(JobTemplatesFullSelectors.isPending(id));
  }

  public uploadCoverImage(id: number, file: File) {
    this.store.dispatch(JobTemplatesFullActions.uploadCoverImage({ payload: { id, file } }));
  }

  public getUploadCoverImageSuccessAction() {
    return this.actions$.pipe(ofType(JobTemplatesFullActions.uploadCoverImageSuccess));
  }

  public getUploadCoverImageFailedAction() {
    return this.actions$.pipe(ofType(JobTemplatesFullActions.uploadCoverImageFailed));
  }

  public updateOne(
    offerTemplateUpdateData: JobTemplatesData & { id: number; adminIds?: number[] },
  ) {
    this.store.dispatch(
      JobTemplatesFullActions.updateOne({ payload: { offerTemplate: offerTemplateUpdateData } }),
    );
  }

  public getUpdateOneSuccessAction() {
    return this.actions$.pipe(ofType(JobTemplatesFullActions.updateOneSuccess));
  }

  public getUpdateOneFailedAction() {
    return this.actions$.pipe(ofType(JobTemplatesFullActions.updateOneFailed));
  }

  public updateName(id: number, name: string) {
    this.store.dispatch(JobTemplatesFullActions.updateName({ payload: { data: { id, name } } }));
  }

  public getUpdateNameSuccessAction() {
    return this.actions$.pipe(ofType(JobTemplatesFullActions.updateNameSuccess));
  }

  public getUpdateNameFailedAction() {
    return this.actions$.pipe(ofType(JobTemplatesFullActions.updateNameFailed));
  }

  public getFetchOneSuccessAction(): Actions<{
    payload: { id: number | string; offer: OfferFull };
  }> {
    return this.actions$.pipe(ofType(JobTemplatesFullActions.fetchOneSuccess));
  }
}
